// App-wide capture of the PWA install lifecycle.
//
// `beforeinstallprompt` fires once, shortly after first load — frequently
// BEFORE a component that wants it (the /install page, the install banner) has
// mounted. A per-component listener therefore misses the event and the native
// install button never appears. We register the listeners at module load
// (imported for side-effect at the top of main.tsx) and stash the event in a
// module-level store that components read synchronously and subscribe to via
// useSyncExternalStore.

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    // Suppress Chrome's default mini-infobar so we can present our own button.
    e.preventDefault();
    deferredPrompt = e;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    // The native install completed — the prompt can no longer be used and the
    // app is now installable-as-installed.
    deferredPrompt = null;
    installed = true;
    emit();
  });
}

/** The stashed install event, or null if the browser hasn't offered one. */
export function getDeferredPrompt(): BeforeInstallPromptEvent | null {
  return deferredPrompt;
}

/** Drop the stashed event (it can only be prompted once). */
export function clearDeferredPrompt(): void {
  deferredPrompt = null;
  emit();
}

/** True once the `appinstalled` event has fired this session. */
export function wasAppInstalled(): boolean {
  return installed;
}

/** Subscribe to install-state changes; returns an unsubscribe fn. */
export function subscribeInstallState(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Test-only: reset module state so tests don't leak install state into each other. */
export function __resetForTests(): void {
  deferredPrompt = null;
  installed = false;
  listeners.clear();
}

/**
 * True when the app is running as an installed PWA (Android/desktop
 * `display-mode: standalone`, or iOS Safari's legacy `navigator.standalone`).
 */
export function isStandaloneDisplay(): boolean {
  if (typeof window === "undefined") return false;
  const mediaStandalone =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(display-mode: standalone)").matches;
  const iosStandalone =
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return mediaStandalone || iosStandalone;
}
